// src/components/layout/Header.jsx
// -----------------------------------------------------------------------------
// Top bar shown on every authenticated page. Contains:
//   - Sidebar collapse/expand toggle
//   - Current page title
//   - Global search box
//   - Notification bell with dropdown menu
//   - Logged-in user menu with Logout
// -----------------------------------------------------------------------------
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import {
  BellIcon,
  KeyIcon,
  UserIcon,
  PlusIcon,
  EditIcon,
  MessageIcon,
  MenuIcon
} from '../common/Icons';
import '../common/common.css';

export default function Header({ title, onToggleSidebar }) {
  const { user, logout } = useAuth();
  const { notifications, unreadCount, markAsRead } = useNotifications();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const navigate = useNavigate();

  const initials = user
    ? `${user.firstName?.[0] || ''}`.toUpperCase()
    : '';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Close dropdowns on clicking anywhere outside
  useEffect(() => {
    const closeDropdowns = () => {
      setMenuOpen(false);
      setNotifOpen(false);
    };
    window.addEventListener('click', closeDropdowns);
    return () => window.removeEventListener('click', closeDropdowns);
  }, []);

  const getNotificationIcon = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('password')) return <KeyIcon size={16} />;
    if (t.includes('assign') || t.includes('team')) return <UserIcon size={16} />;
    if (t.includes('create') || t.includes('new')) return <PlusIcon size={16} />;
    if (t.includes('update') || t.includes('change') || t.includes('status')) return <EditIcon size={16} />;
    if (t.includes('comment')) return <MessageIcon size={16} />;
    return <BellIcon size={16} />;
  };

  const handleNotifClick = async (n) => {
    const id = n.notificationId ?? n.notificationID ?? n.NotificationId ?? n.NotificationID;
    const isRead = n.isReadFlag ?? n.IsReadFlag ?? n.isRead ?? n.IsRead;
    if (!isRead && id) {
      await markAsRead(id);
    }
    setNotifOpen(false);
    navigate('/notifications');
  };

  return (
    <header className="app-header">
      <div className="header-left">
        <button className="sidebar-toggle-btn" onClick={onToggleSidebar} aria-label="Toggle sidebar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <MenuIcon size={20} />
        </button>
        <span className="header-title">
          {title}
        </span>
      </div>

      <div className="header-right">
        {/* Global search - each list page can read the "q" query param */}
        <div className="header-search">
          <input
            type="text"
            placeholder="Search tickets, projects..."
            onKeyDown={(e) => {
              if (e.key === 'Enter') navigate(`/tickets?q=${encodeURIComponent(e.target.value)}`);
            }}
          />
        </div>

        {/* Notifications bell button with dropdown */}
        <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
          <button
            className="header-icon-btn"
            onClick={(e) => {
              e.stopPropagation();
              setNotifOpen((prev) => !prev);
              setMenuOpen(false);
            }}
            aria-label="Notifications"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <BellIcon size={20} />
            {unreadCount > 0 && <span className="dot" />}
          </button>

          {/* Notifications Dropdown Panel */}
          {notifOpen && (
            <div className="card header-notif-dropdown">
              <div className="notif-dropdown-header">
                <h3>Recent Notifications</h3>
                <button
                  className="notif-dropdown-link"
                  onClick={() => {
                    setNotifOpen(false);
                    navigate('/notifications');
                  }}
                >
                  View All
                </button>
              </div>
              <div className="notif-dropdown-list">
                {notifications.length === 0 ? (
                  <div className="notif-dropdown-empty">
                    No notifications yet.
                  </div>
                ) : (
                  notifications.slice(0, 5).map((n) => {
                    const id = n.notificationId ?? n.notificationID ?? n.NotificationId ?? n.NotificationID;
                    const isRead = n.isReadFlag ?? n.IsReadFlag ?? n.isRead ?? n.IsRead;
                    const created = n.createdDttm ?? n.CreatedDttm;
                    const msg = n.message ?? n.Message;
                    const type = n.notificationType ?? n.NotificationType;

                    return (
                      <div
                        key={id}
                        className={`notif-dropdown-item ${!isRead ? 'unread' : ''}`}
                        onClick={() => handleNotifClick(n)}
                      >
                        <div className="notif-dropdown-icon">
                          {getNotificationIcon(type)}
                        </div>
                        <div className="notif-dropdown-content">
                          <p className="notif-dropdown-msg">{msg}</p>
                          <span className="notif-dropdown-time">
                            {new Date(created).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* User menu */}
        <div
          className="header-user"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((o) => !o);
            setNotifOpen(false);
          }}
        >
          <div className="header-user-avatar">{initials || 'U'}</div>
          <div className="header-user-info">
            <span className="name">
              {user?.firstName} {user?.lastName}
            </span>
            <span className="role">{user?.roleName}</span>
          </div>
        </div>

        {menuOpen && (
          <div className="card header-user-dropdown" onClick={(e) => e.stopPropagation()}>
            <button
              className="btn btn-outline"
              style={{ width: '100%', marginBottom: 8 }}
              onClick={() => {
                setMenuOpen(false);
                navigate('/profile');
              }}
            >
              My Profile
            </button>
            <button className="btn btn-outline btn-logout" onClick={handleLogout}>
              Logout
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

Header.propTypes = {
  title: PropTypes.string.isRequired,
  onToggleSidebar: PropTypes.func.isRequired,
};